import { useMemo, useState} from 'react';
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type SortingState,
  type Row,
  type Column
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { MoreHorizontal, Pencil, Trash, ArrowUp, ArrowDown, ArrowUpDown, PlusCircle, Plus } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { useChannelStore } from '@/stores/channelStore';
import { useChannelTypes } from '@/hooks/useChannelTypes';
import { ErrorDisplay } from '@/components/common/ErrorBoundary';
import type { Channel, ChannelNumber } from '../types';
import { getPlanId, getPlanName, getPlanColorIndex } from '../types';
import { getPlanColor } from '@/utils/colors';
import { cn } from '@/lib/utils';
import { useProvider } from '@/hooks/useProviders';
import { formatCurrency } from '@/utils/currencyFormatter';
import { useProviderCurrency } from '@/stores/currencyStore';
import { Badge } from '@/components/ui/badge';


interface ChannelTableProps {
  providerId: number;
  versionId: number;
  channels: Channel[];
  isReadOnly?: boolean;
}

export function ChannelTable({
  providerId,
  versionId,
  channels,
  isReadOnly
}: ChannelTableProps) {
  // Debug provider and types data
  // console.log('[DEBUG] Provider and types:', {
  //   providerId,
  //   versionId,
  //   channels,
  //   isReadOnly
  // });

  // Add debugging for the channels data
  // console.log('[DEBUG] Channels data with numbers:', channels.map(channel => {
  //   // Log each individual number for debugging
  //   if (channel.numbers) {
  //     channel.numbers.forEach((num, idx) => {
  //       console.log(`[DEBUG] Channel ${channel.id} number ${idx}:`, {
  //         id: num.id,
  //         number: num.number,
  //         is_sequence: num.is_sequence,
  //         type_id: num.type_id,
  //         type: num.type
  //       });
  //     });
  //   }
  //   
  //   // Add is_local flag logging
  //   console.log(`[DEBUG] Channel ${channel.id} is_local:`, {
  //     name: channel.name,
  //     is_local: channel.is_local,
  //     is_local_type: typeof channel.is_local
  //   });
  //   
  //   return {
  //     id: channel.id,
  //     name: channel.name,
  //     numbers: channel.numbers?.filter(n => n.is_sequence === true)?.map(n => n.number),
  //     allNumbers: channel.numbers?.length || 0,
  //     hasSequence: channel.numbers?.some(n => n.is_sequence === true) || false,
  //     is_local: channel.is_local
  //   };
  // }));
  
  const { data: types } = useChannelTypes(providerId, versionId);
  const { 
    selectedChannels, 
    toggleChannelSelection, 
    setSelectedChannels,
    openEditModal,
    openDeleteModal
  } = useChannelStore();
  const [sorting, setSorting] = useState<SortingState>([]);

  const [error] = useState<Error | null>(null);
  const { data: provider } = useProvider(providerId);
  // Always call useProviderCurrency, don't put it in numberColumns
  const currency = useProviderCurrency(providerId);

  // Convert selected channels to a map for easier lookup
  const selectedMap = useMemo(() => {
    return Object.entries(selectedChannels).reduce<Record<string, boolean>>((acc, [id, selected]) => {
      acc[id] = selected;
      return acc;
    }, {});
  }, [selectedChannels]);

  // Check if all channels are selected
  const allSelected = useMemo(() => {
    return channels && channels.length > 0 && channels.every(channel => selectedMap[channel.id.toString()]);
  }, [channels, selectedMap]);

  // Check if some channels are selected
  const someSelected = useMemo(() => {
    return channels?.some(channel => selectedMap[channel.id.toString()]) && !allSelected;
  }, [channels, selectedMap, allSelected]);

  // Update the number columns section
  const numberColumns = useMemo(() => {
    if (provider?.has_sequence_numbers) {
      return [{
        id: 'sequence_number',
        header: ({ column }: { column: Column<Channel> }) => (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="-ml-4"
          >
            Sequence Number
            {column.getIsSorted() === "asc" ? (
              <ArrowUp className="ml-2 h-4 w-4" />
            ) : column.getIsSorted() === "desc" ? (
              <ArrowDown className="ml-2 h-4 w-4" />
            ) : (
              <ArrowUpDown className="ml-2 h-4 w-4" />
            )}
          </Button>
        ),
        accessorFn: (row: Channel) => {
          // Instead of filtering, check the type_id against the sequence type
          const sequenceTypeId = types?.find(t => t.is_sequence)?.id;
          
          // Get sequence numbers - either explicitly marked as sequence OR with the sequence type ID
          const sequenceNumbers = row.numbers
            .filter(n => 
              n.is_sequence === true || 
              (sequenceTypeId && n.type_id === sequenceTypeId)
            )
            .map(n => parseInt(n.number || '0'))
            .sort((a, b) => a - b);
          
          // Log detailed information about the sequence numbers for debugging
          // console.log(`[ChannelTable] Sequence numbers for ${row.name}:`, {
          //   channelId: row.id,
          //   allNumbers: row.numbers,
          //   sequenceTypeId,
          //   sequenceNumbers: sequenceNumbers,
          //   sequenceCount: sequenceNumbers.length,
          //   hasSequence: sequenceNumbers.length > 0,
          //   numberDetails: row.numbers.map(n => ({
          //     id: n.id,
          //     number: n.number,
          //     is_sequence: n.is_sequence,
          //     type_id: n.type_id,
          //     type_name: n.type?.name
          //   }))
          // });
          
          // Return the first number for sorting purposes
          return sequenceNumbers.length > 0 ? sequenceNumbers[0] : 0;
        },
        cell: ({ row }: { row: Row<Channel> }) => {
          // Instead of filtering, check the type_id against the sequence type
          const sequenceTypeId = types?.find(t => t.is_sequence)?.id;
          
          // Get sequence numbers - either explicitly marked as sequence OR with the sequence type ID
          const sequenceNumbers = row.original.numbers
            .filter(n => 
              n.is_sequence === true || 
              (sequenceTypeId && n.type_id === sequenceTypeId)
            )
            .map(n => n.number)
            .sort((a, b) => parseInt(a) - parseInt(b));
          
          // console.log(`[Cell] Sequence numbers for ${row.original.name}:`, {
          //   numbers: sequenceNumbers,
          //   originalNumbers: row.original.numbers,
          //   sequenceTypeId
          // });
          
          if (sequenceNumbers.length === 0) {
            return <div className="text-muted-foreground">-</div>;
          }
          
          return (
            <div className="font-medium text-foreground">
              {sequenceNumbers.map((num, index) => (
                <span key={`${row.original.id}-seq-${index}`}>
                  {num}
                  {index < sequenceNumbers.length - 1 ? ', ' : ''}
                </span>
              ))}
            </div>
          );
        },
        sortingFn: (rowA: Row<Channel>, rowB: Row<Channel>, columnId: string) => {
          const a = rowA.getValue(columnId);
          const b = rowB.getValue(columnId);
          return Number(a) - Number(b);
        }
      }];
    } else if (types) {
      return types.map(type => {
        return ({
          id: `number_${type.id}`,
          header: ({ column }: { column: Column<Channel> }) => (
            <Button
              variant="ghost"
              onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
              className="-ml-4"
            >
              {type.name}
              {column.getIsSorted() === "asc" ? (
                <ArrowUp className="ml-2 h-4 w-4" />
              ) : column.getIsSorted() === "desc" ? (
                <ArrowDown className="ml-2 h-4 w-4" />
              ) : (
                <ArrowUpDown className="ml-2 h-4 w-4" />
              )}
            </Button>
          ),
          accessorFn: (row: Channel) => {
            const number = row.numbers.find((n: ChannelNumber) => n.type_id === type.id);
            return number?.number || '';
          },
          cell: ({ row }: { row: Row<Channel> }) => {
            const number = row.original.numbers.find((n: ChannelNumber) => n.type_id === type.id);
            const displayValue = number?.number;

            // Display asterisk if no number exists OR if number is explicitly '*'
            if (!displayValue || displayValue === '*') {
              return <div className="text-muted-foreground">*</div>;
            }

            return (
              <div className="font-medium text-foreground">
                {displayValue}
              </div>
            );
          },
          sortingFn: (rowA: Row<Channel>, rowB: Row<Channel>, columnId: string) => {
            const a = rowA.getValue(columnId);
            const b = rowB.getValue(columnId);

            // Try numeric sorting first
            const numA = Number(a);
            const numB = Number(b);
            if (!isNaN(numA) && !isNaN(numB)) {
              return numA - numB;
            }

            // Fall back to string comparison
            return String(a).localeCompare(String(b));
          }
        });
      });
    } else {
      return [];
    }
  }, [provider, types]);

  // Define table columns
  const columns = useMemo<ColumnDef<Channel>[]>(() => [
    // Selection column
    {
      id: 'select',
      header: () => (
        <Checkbox
          checked={allSelected}
          className={cn(
            someSelected && "data-[state=indeterminate]:bg-primary",
            "transition-colors"
          )}
          data-state={someSelected ? "indeterminate" : allSelected ? "checked" : "unchecked"}
          onCheckedChange={(checked) => {
            const newSelection: Record<string, boolean> = {};
            if (checked && channels) {
              channels.forEach(channel => {
                newSelection[channel.id.toString()] = true;
              });
            }
            setSelectedChannels(newSelection);
          }}
          aria-label="Select all"
        />
      ),
      cell: ({ row }: { row: Row<Channel> }) => (
        <Checkbox
          checked={selectedMap[row.original.id.toString()] || false}
          onCheckedChange={() => {
            toggleChannelSelection(row.original.id);
          }}
          aria-label="Select row"
          className="transition-colors"
        />
      ),
      enableSorting: false
    },
    // Name column with sorting
    {
      id: 'name',
      header: ({ column }) => (
        <div onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')} className="cursor-pointer flex items-center">
          Channel Name
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </div>
      ),
      accessorKey: 'name',
      cell: ({ row }) => (
        <div className="font-medium">
          <div className="flex items-center gap-1 mb-1">
            {row.original.is_duplicate && (
              <Badge 
                variant="destructive" 
                className="text-xs bg-red-500 text-white dark:bg-red-600 dark:text-white py-0 px-2 h-5"
              >
                Duplicate
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            {row.getValue('name')}
            {row.original.is_local && (
              <Badge 
                variant="default" 
                className="text-xs bg-blue-500 text-white dark:bg-blue-600 dark:text-white py-0 px-2 h-5"
              >
                Local
              </Badge>
            )}
          </div>
        </div>
      )
    },
    {
      id: 'price',
      header: 'Price',
      cell: ({ row }: { row: Row<Channel> }) => {
        return (
          <div>
            {formatCurrency(row.original.price, currency)}
          </div>
        );
      }
    },
    {
      id: 'description',
      header: 'Description',
      cell: ({ row }: { row: Row<Channel> }) => (
        <div className="max-w-[300px] truncate">
          {row.original.description || '-'}
        </div>
      )
    },
    ...numberColumns,  // Spread the number columns here
    // Categories column
    {
      id: 'categories',
      header: 'Categories',
      accessorFn: (row: Channel) => {
        // console.log('[ChannelTable] Row categories data:', {
        //   id: row.id,
        //   name: row.name,
        //   categories: row.categories,
        //   legacyCategory: row.category
        // });
        
        // First look for categories from the new structure
        if (row.categories && row.categories.length > 0) {
          // Get all category names, sorted by default first
          return row.categories
            .sort((a, b) => (b.is_default ? 1 : 0) - (a.is_default ? 1 : 0))
            .map(cc => cc.category?.name || '')
            .join(', ');
        }
        
        // Fallback to legacy single category
        return row.category?.name || '';
      },
      cell: ({ row }) => {
        const channel = row.original;
        
        // Debug the categories data
        // console.log(`[ChannelTable] Channel ${channel.id} categories:`, {
        //   hasCategories: Boolean(channel.categories),
        //   categories: channel.categories,
        //   categoryCount: channel.categories?.length || 0
        // });
        
        // First look for categories from the new structure
        if (channel.categories && channel.categories.length > 0) {
          return (
            <div className="flex flex-wrap gap-1 max-w-[200px]">
              {channel.categories.map((cc) => (
                <Badge 
                  key={`${channel.id}-${cc.category_id}`} 
                  variant={cc.is_default ? "default" : "secondary"}
                  className="whitespace-nowrap text-xs"
                >
                  {cc.category?.name || 'Unknown'}
                </Badge>
              ))}
            </div>
          );
        }
        
        // Fallback to legacy single category
        if (channel.category) {
          return (
            <Badge variant="default" className="whitespace-nowrap text-xs">
              {channel.category.name}
            </Badge>
          );
        }
        
        return null;
      },
    },
    // Plans column
    {
      id: 'plans',
      header: 'Plans',
      cell: ({ row }: { row: Row<Channel> }) => (
        <div className="flex gap-1">
          {row.original.plans?.map((plan) => {
            if (!plan) return null;
            
            return (
              <div
                key={`${row.original.id}-${getPlanId(plan)}`}
                className={cn(
                  "w-3 h-3 rounded-full",
                  "border border-border"
                )}
                style={{
                  backgroundColor: getPlanColor(getPlanColorIndex(plan)).light,
                  borderColor: getPlanColor(getPlanColorIndex(plan)).dark
                }}
                title={getPlanName(plan)}
              />
            );
          })}
        </div>
      )
    },
    // Actions column
    {
      id: 'actions',
      cell: ({ row }: { row: Row<Channel> }) => {
        if (isReadOnly) return null;
        
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => openEditModal({
                  ...row.original,
                  provider_id: providerId,
                  version_id: versionId
                })}
              >
                <Pencil className="mr-2 h-4 w-4" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => openDeleteModal({
                  ...row.original,
                  provider_id: providerId,
                  version_id: versionId
                })}
                className="text-destructive"
              >
                <Trash className="mr-2 h-4 w-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      }
    }
  ], [
    toggleChannelSelection, 
    setSelectedChannels, 
    selectedMap, 
    channels, 
    allSelected, 
    someSelected, 
    openEditModal, 
    openDeleteModal, 
    isReadOnly, 
    types, 
    provider,
    providerId, 
    versionId,
    numberColumns,
    currency
  ]);

  // Initialize table
  const table = useReactTable({
    data: channels,
    columns,
    state: {
      sorting,
      rowSelection: selectedMap,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableRowSelection: !isReadOnly,
    enableMultiRowSelection: !isReadOnly,
  });


  // Handle error state
  if (error) {
    return <ErrorDisplay error={error} />;
  }

  // Show empty state if no channels
  if (channels.length === 0) {
    return (
      <div className="rounded-md border border-dashed p-8">
        <div className="flex flex-col items-center justify-center text-center">
          <PlusCircle className="h-10 w-10 text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold">No Channels Available</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Get started by adding your first channel
          </p>
          {!isReadOnly && (
            <Button
              onClick={() => useChannelStore.getState().openCreateModal()}
              variant="outline"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Channel
            </Button>
          )}
        </div>
      </div>
    );
  }

  // Render the table
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow
              key={row.id}
              data-state={selectedMap[row.original.id] ? "selected" : undefined}
            >
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}